
import React, { useState, useMemo, useEffect } from 'react';
import { Student, AttendanceStatus, AttendanceRecord, ScheduleEntry, UserRole, AgendaEntry, ActivityType, Teacher, SchoolSettings, View } from '../types'; // Import Teacher
import { getThemeById } from '../src/themeConfig';
import { Calendar, Users, BarChart3, FileDown, Search, Info, ChevronLeft, ChevronRight, ArrowRight, Clock, FileSpreadsheet, BookOpen, MessageSquare, AlertCircle, CheckCircle2, Printer } from 'lucide-react'; // Changed BarChart to BarChart3
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

interface AttendanceProps {
  students: Student[];
  classes: string[];
  schedules: ScheduleEntry[];
  role: UserRole;
  agendas: AgendaEntry[];
  attendanceRecords: AttendanceRecord[];
  onUpdateAttendance: (records: AttendanceRecord[]) => void;
  activeUser: Teacher; // Added activeUser
  schoolSettings: SchoolSettings;
  initialTab?: 'today' | 'pending' | 'recap';
  onNavigate?: (view: View, params?: any) => void;
  appTheme?: string;
}

interface AttendanceData {
  status: AttendanceStatus;
  description?: string;
}

const statusConfig: Record<string, { label: string; short: string; color: string; activeBg: string; dot: string }> = {
  [AttendanceStatus.Hadir]: { label: 'Hadir', short: 'H', color: 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-400', activeBg: 'bg-green-600 text-white', dot: 'bg-green-500' },
  [AttendanceStatus.Izin]: { label: 'Izin', short: 'I', color: 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-400', activeBg: 'bg-blue-600 text-white', dot: 'bg-blue-500' },
  [AttendanceStatus.Sakit]: { label: 'Sakit', short: 'S', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-400', activeBg: 'bg-yellow-600 text-white', dot: 'bg-yellow-500' },
  [AttendanceStatus.Alpa]: { label: 'Alpa', short: 'A', color: 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-400', activeBg: 'bg-red-600 text-white', dot: 'bg-red-500' },
  [AttendanceStatus.Dispen]: { label: 'Dispen', short: 'D', color: 'bg-purple-100 text-purple-800 dark:bg-purple-500/20 dark:text-purple-400', activeBg: 'bg-purple-600 text-white', dot: 'bg-purple-500' },
  [AttendanceStatus.Terlambat]: { label: 'Terlambat', short: 'T', color: 'bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-400', activeBg: 'bg-orange-600 text-white', dot: 'bg-orange-500' },
  [AttendanceStatus.Pulang]: { label: 'Pulang', short: 'P', color: 'bg-teal-100 text-teal-800 dark:bg-teal-500/20 dark:text-teal-400', activeBg: 'bg-teal-600 text-white', dot: 'bg-teal-500' },
};

const getStatusCfg = (st: any) => {
  if (!st) return statusConfig[AttendanceStatus.Hadir];
  return statusConfig[st as AttendanceStatus] || statusConfig[st.toUpperCase() as AttendanceStatus] || {
    label: st.toString(),
    short: (st.toString()[0] || 'H').toUpperCase(),
    color: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200',
    activeBg: 'bg-slate-600 text-white',
    dot: 'bg-slate-500'
  };
};

const monthNames = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
];

const getStudentNameColor = (gender?: string) => {
  const g = (gender || '').trim().toLowerCase();
  const isFemale = g === 'perempuan' || g === 'p' || g === 'female' || g === 'f';
  return isFemale ? 'text-pink-600 dark:text-pink-400' : 'text-slate-800 dark:text-white';
};

const safeFormatDate = (dateStr: string | null | undefined, options?: Intl.DateTimeFormatOptions, fallback: string = '-') => {
  if (!dateStr) return fallback;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString('id-ID', options);
};

const AttendanceView: React.FC<AttendanceProps> = ({ 
  students, 
  classes, 
  schedules, 
  role, 
  agendas, 
  attendanceRecords, 
  onUpdateAttendance, 
  activeUser, 
  schoolSettings, 
  initialTab,
  onNavigate,
  appTheme = 'wzc-themes'
}) => {
  const currentTheme = useMemo(() => getThemeById(appTheme), [appTheme]);
  const [activeTab, setActiveTab] = useState<'today' | 'pending' | 'recap'>('today');

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedAgendaId, setSelectedAgendaId] = useState<string | null>(null);
  const [recapClassFilter, setRecapClassFilter] = useState<string>(activeUser.assignedClasses[0] || classes[0] || '9A'); // Use activeUser.assignedClasses
  const [recapMonth, setRecapMonth] = useState<string>('SEMUA');
  const [recapYear, setRecapYear] = useState<string>('SEMUA');
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for dispen notes modal
  const [dispenModal, setDispenModal] = useState<{ isOpen: boolean, studentId: string, name: string, tempNotes: string }>({
    isOpen: false,
    studentId: '',
    name: '',
    tempNotes: ''
  });

  // State for recap edit popover
  const [recapEditPopover, setRecapEditPopover] = useState<{
    isOpen: boolean;
    studentId: string;
    agendaId: string;
    studentName: string;
    currentStatus: AttendanceStatus | null;
    x: number;
    y: number;
  }>({
    isOpen: false,
    studentId: '',
    agendaId: '',
    studentName: '',
    currentStatus: null,
    x: 0,
    y: 0
  });

  const dayOfWeek = useMemo(() => {
    const d = new Date(selectedDate);
    const validD = isNaN(d.getTime()) ? new Date() : d;
    const names: string[] = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
    return names[validD.getDay()];
  }, [selectedDate]);


  const agendasToday = useMemo(() => {
    return agendas.filter(a => {
      // Exclude extra agendas without a class (SUMATIF) or all SAS agendas
      const isExtraAgendaWithoutClass = a.activityType === ActivityType.SUMATIF && (!a.class || a.class === '');
      const isSas = a.activityType === ActivityType.SAS || (a.activityType as any) === 'SAS' || (a.activityType as any) === 'Sumatif Akhir';
      // Exclude 'Jam kosong' status
      const isJamKosong = a.status === 'Jam kosong';
      const isOptional = a.isAttendanceOptional;
      
      if (isExtraAgendaWithoutClass || isSas || isJamKosong || isOptional) return false;

      return a.date === selectedDate &&
        (activeUser.assignedClasses.includes(a.class) || (a.startPeriod === 0 && a.subject === activeUser.subject)) && // Filter by assigned classes OR additional agendas
        a.subject === activeUser.subject; // Filter by teacher's subject
    }).sort((a, b) => a.startPeriod - b.startPeriod);
  }, [agendas, selectedDate, activeUser]);

  const filteredStudents = useMemo(() => {
    if (!selectedClass) return [];
    return students.filter(s => {
      const isClassAndStatusMatch = s.class === selectedClass && s.status === 'Aktif';
      if (!isClassAndStatusMatch) return false;
      const name = (s.name || '').toLowerCase();
      const term = (searchTerm || '').toLowerCase();
      return name.includes(term);
    }).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'id', { sensitivity: 'base' }));
  }, [students, selectedClass, searchTerm]);

  const handleStatusChange = (studentId: string, status: AttendanceStatus) => {
    if (!selectedAgendaId) return;
    
    if (status === AttendanceStatus.Dispen) {
        const student = students.find(s => s.id === studentId);
        const existingRecord = attendanceRecords.find(r => r.agendaId === selectedAgendaId && r.studentId === studentId);
        setDispenModal({ isOpen: true, studentId, name: student?.name || 'Siswa', tempNotes: existingRecord?.description || '' });
    } else {
        const otherRecords = attendanceRecords.filter(r => !(r.agendaId === selectedAgendaId && r.studentId === studentId));
        const student = students.find(s => s.id === studentId);
        const newRecord: AttendanceRecord = {
          id: `att-${selectedAgendaId}-${studentId}`,
          studentId,
          agendaId: selectedAgendaId,
          date: selectedDate,
          status: status,
          class: student?.class || null,
          teacherId: activeUser.id
        };
        onUpdateAttendance([...otherRecords, newRecord]);
    }
  };

  const handleSaveDispenNotes = (studentId: string, description: string) => {
    if (selectedAgendaId) {
        const student = students.find(s => s.id === studentId);
        const otherRecords = attendanceRecords.filter(r => !(r.agendaId === selectedAgendaId && r.studentId === studentId));
        const newRecord: AttendanceRecord = {
          id: `att-${selectedAgendaId}-${studentId}`,
          studentId: studentId,
          agendaId: selectedAgendaId,
          date: selectedDate,
          status: AttendanceStatus.Dispen,
          description: description,
          class: student?.class || null,
          teacherId: activeUser.id
        };
        onUpdateAttendance([...otherRecords, newRecord]);
        setDispenModal({ ...dispenModal, isOpen: false });
    }
  };

  const handleRecapStatusUpdate = (studentId: string, agendaId: string, status: AttendanceStatus) => {
    const agenda = agendas.find(a => a.id === agendaId);
    if (!agenda) return;

    if (status === AttendanceStatus.Dispen) {
        const student = students.find(s => s.id === studentId);
        const existingRecord = attendanceRecords.find(r => r.agendaId === agendaId && r.studentId === studentId);
        
        setSelectedAgendaId(agendaId);
        setSelectedDate(agenda.date);
        
        setDispenModal({ 
            isOpen: true, 
            studentId, 
            name: student?.name || 'Siswa', 
            tempNotes: existingRecord?.description || '' 
        });
        setRecapEditPopover(prev => ({ ...prev, isOpen: false }));
    } else {
        const otherRecords = attendanceRecords.filter(r => !(r.agendaId === agendaId && r.studentId === studentId));
        const student = students.find(s => s.id === studentId);
        
        const newRecord: AttendanceRecord = {
          id: `att-${agendaId}-${studentId}`,
          studentId,
          agendaId,
          date: agenda.date,
          status: status,
          class: student?.class || null,
          teacherId: activeUser.id
        };
        
        onUpdateAttendance([...otherRecords, newRecord]);
        setRecapEditPopover(prev => ({ ...prev, isOpen: false }));
    }
  };

  const handleSaveAllAttendance = () => {
    if (!selectedAgendaId || !selectedClass) return;

    const currentClassStudents = students.filter(s => s.class === selectedClass && s.status === 'Aktif');
    const existingRecordsForAgenda = attendanceRecords.filter(r => r.agendaId === selectedAgendaId);
    
    const newRecords: AttendanceRecord[] = [];
    
    currentClassStudents.forEach(student => {
      const hasRecord = existingRecordsForAgenda.some(r => r.studentId === student.id);
      if (!hasRecord) {
        newRecords.push({
          id: `att-${selectedAgendaId}-${student.id}`,
          studentId: student.id,
          agendaId: selectedAgendaId,
          date: selectedDate,
          status: AttendanceStatus.Hadir,
          class: student.class,
          teacherId: activeUser.id
        });
      }
    });

    if (newRecords.length > 0) {
      onUpdateAttendance([...attendanceRecords, ...newRecords]);
    }
    
    setSelectedClass(null);
    setSelectedAgendaId(null);
  };

  // LOGIC REKAP MATRIKS
  const recapData = useMemo(() => {
    const recapClassNormalized = recapClassFilter.trim().toLowerCase();
    const teacherSubjectNormalized = (activeUser.subject || '').trim().toLowerCase();

    const studentsInClass = students.filter(s => {
        const sClass = (s.class || '').trim().toLowerCase();
        return sClass === recapClassNormalized && s.status === 'Aktif';
    }).sort((a,b) => a.name.localeCompare(b.name));

    const classAgendas = agendas
        .filter(a => {
            const agClass = (a.class || '').trim().toLowerCase();
            const agSubject = (a.subject || '').trim().toLowerCase();
            
            const matchClass = agClass === recapClassNormalized;
            const matchSubject = agSubject === teacherSubjectNormalized;
            
            const agendaDate = new Date(a.date);
            const matchMonth = recapMonth === 'SEMUA' || (agendaDate.getMonth() + 1).toString() === recapMonth;
            const matchYear = recapYear === 'SEMUA' || agendaDate.getFullYear().toString() === recapYear;
            
            // NEW: Exclude Jam Kosong and activities that don't need attendance
            const isJamKosong = a.status === 'Jam kosong';
            const isNoAbsenceActivity = [
              ActivityType.KEGIATAN_LOMBA, 
              ActivityType.KEGIATAN_KESISWAAN, 
              ActivityType.KEGIATAN_SEKOLAH, 
              ActivityType.SAS
            ].includes(a.activityType as any) || 
            (a.activityType as any) === 'SAS' || 
            (a.activityType as any) === 'Sumatif Akhir' ||
            a.isAttendanceOptional;

            // Check if there are any attendance records for this agenda
            const hasAttendance = attendanceRecords.some(r => r.agendaId === a.id);
            
            return matchClass && matchSubject && matchMonth && matchYear && hasAttendance && !isJamKosong && !isNoAbsenceActivity;
        })
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    
    const rows = studentsInClass.map(student => {
        const counts = { H: 0, S: 0, I: 0, A: 0, D: 0 };
        const historyMap: Record<string, { status: AttendanceStatus, description?: string } | null> = {};

        classAgendas.forEach(agenda => {
            const record = attendanceRecords.find(r => r.studentId === student.id && r.agendaId === agenda.id);
            const status = record ? record.status : null;
            historyMap[agenda.id] = record ? { status: record.status, description: record.description } : null;
            
            if (status === AttendanceStatus.Hadir || status === AttendanceStatus.Terlambat || status === AttendanceStatus.Pulang) counts.H++;
            else if (status === AttendanceStatus.Sakit) counts.S++;
            else if (status === AttendanceStatus.Izin) counts.I++;
            else if (status === AttendanceStatus.Alpa) counts.A++;
            else if (status === AttendanceStatus.Dispen) counts.D++;
        });

        const totalAgendas = classAgendas.length;
        const percentages = {
            H: totalAgendas > 0 ? (counts.H / totalAgendas) * 100 : 0,
            S: totalAgendas > 0 ? (counts.S / totalAgendas) * 100 : 0,
            I: totalAgendas > 0 ? (counts.I / totalAgendas) * 100 : 0,
            A: totalAgendas > 0 ? (counts.A / totalAgendas) * 100 : 0,
            D: totalAgendas > 0 ? (counts.D / totalAgendas) * 100 : 0,
        };

        return {
            ...student,
            history: historyMap,
            counts,
            percentages
        };
    });

    // Class Statistics (Summary)
    const totalPossibleRecords = studentsInClass.length * classAgendas.length;
    const classStats = {
        totalH: rows.reduce((acc, row) => acc + row.counts.H, 0),
        totalS: rows.reduce((acc, row) => acc + row.counts.S, 0),
        totalI: rows.reduce((acc, row) => acc + row.counts.I, 0),
        totalA: rows.reduce((acc, row) => acc + row.counts.A, 0),
        totalD: rows.reduce((acc, row) => acc + row.counts.D, 0),
    };

    const classPercentages = {
        attendance: totalPossibleRecords > 0 ? (classStats.totalH / totalPossibleRecords) * 100 : 0,
        sick: totalPossibleRecords > 0 ? (classStats.totalS / totalPossibleRecords) * 100 : 0,
        permission: totalPossibleRecords > 0 ? (classStats.totalI / totalPossibleRecords) * 100 : 0,
        alpa: totalPossibleRecords > 0 ? (classStats.totalA / totalPossibleRecords) * 100 : 0,
        average: 0 
    };
    
    classPercentages.average = (classPercentages.sick + classPercentages.permission + classPercentages.alpa) / 3;

    return { 
        agendas: classAgendas, 
        rows,
        classStats,
        classPercentages
    };
  }, [students, recapClassFilter, agendas, attendanceRecords, activeUser, recapMonth, recapYear]);

  const handlePrintAttendance = () => {
    if (recapData.rows.length === 0) {
      alert('Tidak ada data untuk dicetak.');
      return;
    }

    const today = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Mohon izinkan pop-up untuk mencetak.');
      return;
    }
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Cetak Rekap Absensi</title>
        <style>
          @page { size: portrait; margin: 1cm; }
          body { font-family: 'Times New Roman', Times, serif; color: #000; margin: 0; padding: 10px; font-size: 9pt; }
          .page-break { page-break-after: always; }
          .kop-container { display: flex; align-items: center; justify-content: space-between; margin-bottom: 5px; text-align: center; }
          .kop-logo { width: 60px; height: 60px; object-fit: contain; }
          .kop-text { flex: 1; text-align: center; }
          .kop-text p { margin: 0; line-height: 1; }
          .kop-gov { font-size: 11pt; font-weight: bold; text-transform: uppercase; }
          .kop-dept { font-size: 11pt; font-weight: bold; text-transform: uppercase; }
          .kop-school { font-size: 14pt; font-weight: bold; text-transform: uppercase; }
          .kop-address { font-size: 8pt; font-style: italic; }
          .kop-additional { font-size: 8pt; }
          .kop-line-1 { height: 2px; background-color: #000; width: 100%; margin-top: 5px; }
          .kop-line-2 { height: 1px; background-color: #000; width: 100%; margin-top: 1px; margin-bottom: 5px; }
          .title { text-align: center; margin: 10px 0; font-weight: bold; font-size: 11pt; text-transform: uppercase; }
          .meta-info { width: 100%; margin-bottom: 10px; font-size: 9pt; }
          .meta-info td { padding: 1px 2px; vertical-align: top; }
          table.data-table { width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 8pt; }
          table.data-table th, table.data-table td { border: 1px solid #000; padding: 2px; text-align: center; }
          table.data-table th { background-color: #7c3aed; color: #ffffff; font-weight: bold; }
          .signature-area { width: 100%; margin-top: 10px; page-break-inside: avoid; }
          .signature-area td { width: 50%; text-align: center; vertical-align: bottom; height: 70px; }
          .signature-name { font-weight: bold; text-decoration: underline; }
        </style>
      </head>
      <body>
          ${generateAttendanceHtml(recapData, recapClassFilter, today)}
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.close();
            }, 500);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handlePrintAllClasses = () => {
    const today = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Mohon izinkan pop-up untuk mencetak.');
      return;
    }

    let allClassesHtml = '';

    activeUser.assignedClasses.forEach((cls) => {
        const recapData = computeRecapData(cls, recapMonth, recapYear);
        if (recapData.rows.length === 0) return;
        
        allClassesHtml += generateAttendanceHtml(recapData, cls, today);
    });

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Cetak Semua Rekap Absensi</title>
        <style>
          @page { size: portrait; margin: 1cm; }
          body { font-family: 'Times New Roman', Times, serif; color: #000; margin: 0; padding: 10px; font-size: 9pt; }
          .page-break { page-break-after: always; }
          .kop-container { display: flex; align-items: center; justify-content: space-between; margin-bottom: 5px; text-align: center; }
          .kop-logo { width: 60px; height: 60px; object-fit: contain; }
          .kop-text { flex: 1; text-align: center; }
          .kop-text p { margin: 0; line-height: 1; }
          .kop-gov { font-size: 11pt; font-weight: bold; text-transform: uppercase; }
          .kop-dept { font-size: 11pt; font-weight: bold; text-transform: uppercase; }
          .kop-school { font-size: 14pt; font-weight: bold; text-transform: uppercase; }
          .kop-address { font-size: 8pt; font-style: italic; }
          .kop-additional { font-size: 8pt; }
          .kop-line-1 { height: 2px; background-color: #000; width: 100%; margin-top: 5px; }
          .kop-line-2 { height: 1px; background-color: #000; width: 100%; margin-top: 1px; margin-bottom: 5px; }
          .title { text-align: center; margin: 10px 0; font-weight: bold; font-size: 11pt; text-transform: uppercase; }
          .meta-info { width: 100%; margin-bottom: 10px; font-size: 9pt; }
          .meta-info td { padding: 1px 2px; vertical-align: top; }
          .meta-info td:nth-child(1) { width: 15%; }
          .meta-info td:nth-child(2) { width: 2%; }
          .meta-info td:nth-child(3) { width: 33%; }
          .meta-info td:nth-child(4) { width: 15%; }
          .meta-info td:nth-child(5) { width: 2%; }
          .meta-info td:nth-child(6) { width: 33%; }
          table.data-table { width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 8pt; }
          table.data-table th, table.data-table td { border: 1px solid #000; padding: 2px; text-align: center; }
          table.data-table th { background-color: #7c3aed; color: #ffffff; font-weight: bold; }
          table.data-table td.left-align { text-align: left; }
          .bg-hatch-red {
            background: repeating-linear-gradient(45deg, #fee2e2, #fee2e2 5px, #fecaca 5px, #fecaca 10px) !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .status-Alpa { background-color: #FECACA !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .status-Izin { background-color: #BBF7D0 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .status-Sakit { background-color: #FEF9C3 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .status-Dispen { background-color: #BFDBFE !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .signature-area { width: 100%; margin-top: 10px; page-break-inside: avoid; }
          .signature-area td { width: 50%; text-align: center; vertical-align: bottom; height: 70px; }
          .signature-name { font-weight: bold; text-decoration: underline; }
          @media print {
            .no-print { display: none; }
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; padding: 0; }
          }
        </style>
      </head>
      <body>
        ${allClassesHtml}
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.close();
            }, 500);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const getKopHtml = () => `
    <div class="kop-container">
      <img src="${schoolSettings.departmentLogoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Logo_Kabupaten_Ciamis.png/800px-Logo_Kabupaten_Ciamis.png'}" class="kop-logo" alt="Logo Dinas" referrerPolicy="no-referrer" />
      <div class="kop-text">
        <p class="kop-gov">${schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS'}</p>
        <p class="kop-dept">${schoolSettings.departmentName || 'DINAS PENDIDIKAN'}</p>
        <p class="kop-school">${schoolSettings.schoolName || 'SMP NEGERI 1 KAWALI'}</p>
        <p class="kop-address">${schoolSettings.address || 'Jl. Raya Kawali No. 1, Kawali, Ciamis, Jawa Barat 46253'}</p>
        ${schoolSettings.additionalKopLine ? `<p class="kop-additional">${schoolSettings.additionalKopLine}</p>` : ''}
      </div>
      <img src="${schoolSettings.logoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9c/Logo_Kemdikbud.png/800px-Logo_Kemdikbud.png'}" class="kop-logo" alt="Logo Sekolah" referrerPolicy="no-referrer" />
    </div>
    <div class="kop-line-1"></div>
    <div class="kop-line-2"></div>
  `;

  const generateAttendanceHtml = (recapData: any, className: string, today: string) => `
    <div class="page-break">
        ${getKopHtml()}
        <div class="title">
          REKAPITULASI KEHADIRAN SISWA<br/>
          KELAS: ${className}<br/>
          ${recapMonth === 'SEMUA' && recapYear === 'SEMUA' 
            ? 'Semua Jadwal' 
            : `BULAN: ${recapMonth === 'SEMUA' ? 'SEMUA BULAN' : monthNames[parseInt(recapMonth) - 1].toUpperCase()} TAHUN ${recapYear}`}
        </div>

        <table class="meta-info">
          <tr>
            <td>Mata Pelajaran</td><td>:</td><td>${activeUser.subject}</td>
            <td>Semester</td><td>:</td><td>${schoolSettings.semester || '2 (Genap)'}</td>
          </tr>
          <tr>
            <td>Nama Guru</td><td>:</td><td>${activeUser.name}</td>
            <td>Tahun Pelajaran</td><td>:</td><td>${schoolSettings.academicYear || '2025/2026'}</td>
          </tr>
          <tr>
            <td>Kelas</td><td>:</td><td>${className}</td>
            <td></td><td></td><td></td>
          </tr>
        </table>

        <table class="data-table">
          <thead>
            <tr>
              <th rowspan="2">NO</th>
              <th rowspan="2">NISN</th>
              <th rowspan="2">NAMA SISWA</th>
              <th colspan="${recapData.agendas.length || 1}">TANGGAL PERTEMUAN</th>
              <th colspan="5">JUMLAH</th>
            </tr>
            <tr>
              ${recapData.agendas.length > 0 ? recapData.agendas.map((a: any) => `<th class="${a.status === 'Jam kosong' ? 'bg-hatch-red' : ''}">${new Date(a.date).toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit' })}</th>`).join('') : '<th>-</th>'}
              <th>H</th><th>S</th><th>I</th><th>A</th><th>D</th>
            </tr>
          </thead>
          <tbody>
            ${recapData.rows.map((row: any, idx: number) => `
              <tr>
                <td>${idx + 1}</td>
                <td>${row.nisn || row.nis || '-'}</td>
                <td class="left-align">${row.name.toUpperCase()}</td>
                ${recapData.agendas.length > 0 ? recapData.agendas.map((a: any) => `<td class="${a.status === 'Jam kosong' ? 'bg-hatch-red' : ''} ${row.history[a.id]?.status ? 'status-' + row.history[a.id]!.status : ''}">${row.history[a.id]?.status ? getStatusCfg(row.history[a.id]!.status).short : '-'}</td>`).join('') : '<td>-</td>'}
                <td>${row.counts.H}</td>
                <td>${row.counts.S}</td>
                <td>${row.counts.I}</td>
                <td>${row.counts.A}</td>
                <td>${row.counts.D}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <table class="stats-table" style="width: 300px; border-collapse: collapse; margin-top: 15px; margin-bottom: 15px; font-size: 8pt; page-break-inside: avoid;">
          <tbody>
            <tr>
              <td style="border: 1px solid #000; padding: 4px 8px; text-align: left; font-weight: bold; background-color: #f3f4f6; width: 65%;">PERSENTASE ABSENSI</td>
              <td style="border: 1px solid #000; padding: 4px 8px; text-align: center; font-weight: bold; font-size: 9pt; width: 35%;">${recapData.classPercentages.attendance.toFixed(1)}%</td>
            </tr>
            <tr>
              <td style="border: 1px solid #000; padding: 4px 8px; text-align: left; font-weight: bold; background-color: #f3f4f6;">PERSENTASE IZIN</td>
              <td style="border: 1px solid #000; padding: 4px 8px; text-align: center; font-weight: bold; font-size: 9pt;">${recapData.classPercentages.permission.toFixed(1)}%</td>
            </tr>
            <tr>
              <td style="border: 1px solid #000; padding: 4px 8px; text-align: left; font-weight: bold; background-color: #f3f4f6;">PERSENTASE SAKIT</td>
              <td style="border: 1px solid #000; padding: 4px 8px; text-align: center; font-weight: bold; font-size: 9pt;">${recapData.classPercentages.sick.toFixed(1)}%</td>
            </tr>
            <tr>
              <td style="border: 1px solid #000; padding: 4px 8px; text-align: left; font-weight: bold; background-color: #f3f4f6;">PERSENTASE ALPA</td>
              <td style="border: 1px solid #000; padding: 4px 8px; text-align: center; font-weight: bold; font-size: 9pt;">${recapData.classPercentages.alpa.toFixed(1)}%</td>
            </tr>
            <tr style="border-top: 2px solid #000;">
              <td style="border: 1px solid #000; padding: 4px 8px; text-align: left; font-weight: bold; background-color: #f3f4f6;">RATA-RATA REKAP</td>
              <td style="border: 1px solid #000; padding: 4px 8px; text-align: center; font-weight: bold; font-size: 9pt;">${recapData.classPercentages.average.toFixed(1)}%</td>
            </tr>
          </tbody>
        </table>
        
        <table class="signature-area">
          <tr>
            <td>
            </td>
            <td>
              Kawali, ${today}<br/>
              Guru Mata Pelajaran<br/>
              <br/><br/><br/><br/>
              <span class="signature-name">${activeUser.name}</span><br/>
            </td>
          </tr>
        </table>
    </div>
  `;

  const computeRecapData = (className: string, month: string, year: string) => {
    // [COPIED LOGIC FROM lines 240-337]
    const recapClassNormalized = className.trim().toLowerCase();
    const teacherSubjectNormalized = (activeUser.subject || '').trim().toLowerCase();

    const studentsInClass = students.filter(s => {
        const sClass = (s.class || '').trim().toLowerCase();
        return sClass === recapClassNormalized && s.status === 'Aktif';
    }).sort((a,b) => a.name.localeCompare(b.name));

    const classAgendas = agendas
        .filter(a => {
            const agClass = (a.class || '').trim().toLowerCase();
            const agSubject = (a.subject || '').trim().toLowerCase();
            
            const matchClass = agClass === recapClassNormalized;
            const matchSubject = agSubject === teacherSubjectNormalized;
            
            const agendaDate = new Date(a.date);
            const matchMonth = month === 'SEMUA' || (agendaDate.getMonth() + 1).toString() === month;
            const matchYear = year === 'SEMUA' || agendaDate.getFullYear().toString() === year;
            
            // NEW: Exclude Jam Kosong and activities that don't need attendance
            const isJamKosong = a.status === 'Jam kosong';
            const isNoAbsenceActivity = [
              ActivityType.KEGIATAN_LOMBA, 
              ActivityType.KEGIATAN_KESISWAAN, 
              ActivityType.KEGIATAN_SEKOLAH, 
              ActivityType.SAS
            ].includes(a.activityType as any) || 
            (a.activityType as any) === 'SAS' || 
            (a.activityType as any) === 'Sumatif Akhir' ||
            a.isAttendanceOptional;

            // Check if there are any attendance records for this agenda
            const hasAttendance = attendanceRecords.some(r => r.agendaId === a.id);
            
            return matchClass && matchSubject && matchMonth && matchYear && hasAttendance && !isJamKosong && !isNoAbsenceActivity;
        })
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    
    const rows = studentsInClass.map(student => {
        const counts = { H: 0, S: 0, I: 0, A: 0, D: 0 };
        const historyMap: Record<string, { status: AttendanceStatus, description?: string } | null> = {};

        classAgendas.forEach(agenda => {
            const record = attendanceRecords.find(r => r.studentId === student.id && r.agendaId === agenda.id);
            const status = record ? record.status : null;
            historyMap[agenda.id] = record ? { status: record.status, description: record.description } : null;
            
            if (status === AttendanceStatus.Hadir || status === AttendanceStatus.Terlambat || status === AttendanceStatus.Pulang) counts.H++;
            else if (status === AttendanceStatus.Sakit) counts.S++;
            else if (status === AttendanceStatus.Izin) counts.I++;
            else if (status === AttendanceStatus.Alpa) counts.A++;
            else if (status === AttendanceStatus.Dispen) counts.D++;
        });

        const totalAgendas = classAgendas.length;
        const percentages = {
            H: totalAgendas > 0 ? (counts.H / totalAgendas) * 100 : 0,
            S: totalAgendas > 0 ? (counts.S / totalAgendas) * 100 : 0,
            I: totalAgendas > 0 ? (counts.I / totalAgendas) * 100 : 0,
            A: totalAgendas > 0 ? (counts.A / totalAgendas) * 100 : 0,
            D: totalAgendas > 0 ? (counts.D / totalAgendas) * 100 : 0,
        };

        return {
            ...student,
            history: historyMap,
            counts,
            percentages
        };
    });

    // Class Statistics (Summary)
    const totalPossibleRecords = studentsInClass.length * classAgendas.length;
    const classStats = {
        totalH: rows.reduce((acc, row) => acc + row.counts.H, 0),
        totalS: rows.reduce((acc, row) => acc + row.counts.S, 0),
        totalI: rows.reduce((acc, row) => acc + row.counts.I, 0),
        totalA: rows.reduce((acc, row) => acc + row.counts.A, 0),
        totalD: rows.reduce((acc, row) => acc + row.counts.D, 0),
    };

    const classPercentages = {
        attendance: totalPossibleRecords > 0 ? (classStats.totalH / totalPossibleRecords) * 100 : 0,
        sick: totalPossibleRecords > 0 ? (classStats.totalS / totalPossibleRecords) * 100 : 0,
        permission: totalPossibleRecords > 0 ? (classStats.totalI / totalPossibleRecords) * 100 : 0,
        alpa: totalPossibleRecords > 0 ? (classStats.totalA / totalPossibleRecords) * 100 : 0,
        average: 0 
    };
    
    classPercentages.average = (classPercentages.sick + classPercentages.permission + classPercentages.alpa) / 3;

    return { 
        agendas: classAgendas, 
        rows,
        classStats,
        classPercentages
    };
  }

  const handleExportExcel = async () => {
    if (recapData.rows.length === 0) {
      alert('Tidak ada data untuk diekspor.');
      return;
    }

    const today = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Rekap_Absensi');
    worksheet.pageSetup = { paperSize: 9, orientation: 'portrait' };

    // Set column widths
    const cols = [
      { width: 5 },   // A: No
      { width: 12 },  // B: NIS
      { width: 35 },  // C: Nama Siswa
    ];
    recapData.agendas.forEach(() => cols.push({ width: 6 })); // Dates
    cols.push({ width: 5 }, { width: 5 }, { width: 5 }, { width: 5 }, { width: 5 }, { width: 6 }); // H, S, I, A, D, TOTAL
    worksheet.columns = cols;

    const lastColIndex = 3 + recapData.agendas.length + 6;
    const lastColLetter = worksheet.getColumn(lastColIndex).letter;

    // 1. Prepare Header (Kop Sekolah)
    const addImage = async (url: string, col: number, row: number) => {
      try {
        let base64Data = '';
        let extension = 'png';
        let imgWidth = 80;
        let imgHeight = 80;
        
        if (url.startsWith('data:image')) {
          base64Data = url.split(',')[1];
          const mime = url.substring(5, url.indexOf(';'));
          extension = mime.split('/')[1] || 'png';
          
          await new Promise<void>((resolve) => {
            const img = new window.Image();
            img.onload = () => {
              imgWidth = img.width;
              imgHeight = img.height;
              resolve();
            };
            img.onerror = () => resolve();
            img.src = url;
          });
        } else {
          const response = await fetch(url);
          const blob = await response.blob();
          base64Data = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
            reader.readAsDataURL(blob);
          });
          extension = blob.type.split('/')[1] || 'png';
          
          await new Promise<void>((resolve) => {
            const img = new window.Image();
            img.onload = () => {
              imgWidth = img.width;
              imgHeight = img.height;
              URL.revokeObjectURL(img.src);
              resolve();
            };
            img.onerror = () => {
              URL.revokeObjectURL(img.src);
              resolve();
            };
            img.src = URL.createObjectURL(blob);
          });
        }

        const logoId = workbook.addImage({
          base64: base64Data,
          extension: extension as any,
        });
        
        const targetHeight = 70;
        const targetWidth = (imgWidth / imgHeight) * targetHeight;
        
        worksheet.addImage(logoId, {
          tl: { col: col, row: row },
          ext: { width: targetWidth, height: targetHeight }
        });
      } catch (error) {
        console.error('Failed to add logo to excel:', error);
      }
    };

    if (schoolSettings.departmentLogoUrl) {
      await addImage(schoolSettings.departmentLogoUrl, 0.2, 0.2);
    }
    if (schoolSettings.logoUrl) {
      // Position school logo symmetrically on the right
      // lastColIndex is dynamic, so we calculate a good position
      await addImage(schoolSettings.logoUrl, lastColIndex - 1.5, 0.2);
    }

    worksheet.mergeCells(`D1:${lastColLetter}1`);
    worksheet.getCell('D1').value = schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS';
    worksheet.getCell('D1').font = { bold: true, size: 12 };
    worksheet.getCell('D1').alignment = { horizontal: 'center' };

    worksheet.mergeCells(`D2:${lastColLetter}2`);
    worksheet.getCell('D2').value = schoolSettings.departmentName || 'DINAS PENDIDIKAN';
    worksheet.getCell('D2').font = { bold: true, size: 12 };
    worksheet.getCell('D2').alignment = { horizontal: 'center' };

    worksheet.mergeCells(`D3:${lastColLetter}3`);
    worksheet.getCell('D3').value = schoolSettings.schoolName.toUpperCase();
    worksheet.getCell('D3').font = { bold: true, size: 16 };
    worksheet.getCell('D3').alignment = { horizontal: 'center' };

    worksheet.mergeCells(`D4:${lastColLetter}4`);
    worksheet.getCell('D4').value = schoolSettings.address;
    worksheet.getCell('D4').font = { italic: true, size: 10 };
    worksheet.getCell('D4').alignment = { horizontal: 'center' };

    let currentRow = 5;
    if (schoolSettings.additionalKopLine) {
      worksheet.mergeCells(`D${currentRow}:${lastColLetter}${currentRow}`);
      worksheet.getCell(`D${currentRow}`).value = schoolSettings.additionalKopLine;
      worksheet.getCell(`D${currentRow}`).font = { size: 9 };
      worksheet.getCell(`D${currentRow}`).alignment = { horizontal: 'center' };
      currentRow++;
    }

    // Lines
    worksheet.mergeCells(`A${currentRow}:${lastColLetter}${currentRow}`);
    worksheet.getCell(`A${currentRow}`).border = { bottom: { style: 'thick' } };
    currentRow++;
    worksheet.mergeCells(`A${currentRow}:${lastColLetter}${currentRow}`);
    worksheet.getCell(`A${currentRow}`).border = { bottom: { style: 'thin' } };
    currentRow++;

    currentRow++; // Empty row
    
    // Title
    worksheet.mergeCells(`A${currentRow}:${lastColLetter}${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'REKAPITULASI KEHADIRAN SISWA';
    worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 14 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center' };
    currentRow++;

    worksheet.mergeCells(`A${currentRow}:${lastColLetter}${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = recapMonth === 'SEMUA' && recapYear === 'SEMUA' 
      ? 'SEMUA JADWAL' 
      : `BULAN: ${recapMonth === 'SEMUA' ? 'SEMUA BULAN' : monthNames[parseInt(recapMonth) - 1].toUpperCase()} TAHUN ${recapYear}`;
    worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 12 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center' };
    currentRow++;

    currentRow++; // Empty row
    
    // Meta Data
    const metaStartRow = currentRow;
    worksheet.getCell(`A${currentRow}`).value = 'Mata Pelajaran';
    worksheet.getCell(`B${currentRow}`).value = ': ' + activeUser.subject;
    worksheet.getCell(`D${currentRow}`).value = 'Semester';
    worksheet.getCell(`E${currentRow}`).value = ': ' + (schoolSettings.semester || '2 (Genap)');
    currentRow++;

    worksheet.getCell(`A${currentRow}`).value = 'Nama Guru';
    worksheet.getCell(`B${currentRow}`).value = ': ' + activeUser.name;
    worksheet.getCell(`D${currentRow}`).value = 'Tahun Pelajaran';
    worksheet.getCell(`E${currentRow}`).value = ': ' + (schoolSettings.academicYear || '2025/2026');
    currentRow++;

    worksheet.getCell(`A${currentRow}`).value = 'Kelas';
    worksheet.getCell(`B${currentRow}`).value = ': ' + recapClassFilter;
    currentRow++;

    // Table Header
    const tableHeaderRow1 = currentRow + 1;
    const tableHeaderRow2 = currentRow + 2;
    
    worksheet.mergeCells(`A${tableHeaderRow1}:A${tableHeaderRow2}`);
    worksheet.getCell(`A${tableHeaderRow1}`).value = 'NO';
    worksheet.getCell(`A${tableHeaderRow1}`).alignment = { horizontal: 'center', vertical: 'middle' };
    
    worksheet.mergeCells(`C${tableHeaderRow1}:C${tableHeaderRow2}`);
    worksheet.getCell(`C${tableHeaderRow1}`).value = 'NAMA SISWA';
    worksheet.getCell(`C${tableHeaderRow1}`).alignment = { horizontal: 'center', vertical: 'middle' };

    worksheet.mergeCells(`B${tableHeaderRow1}:B${tableHeaderRow2}`);
    worksheet.getCell(`B${tableHeaderRow1}`).value = 'NISN';
    worksheet.getCell(`B${tableHeaderRow1}`).alignment = { horizontal: 'center', vertical: 'middle' };

    if (recapData.agendas.length > 0) {
      const startCol = 4;
      const endCol = 3 + recapData.agendas.length;
      worksheet.mergeCells(tableHeaderRow1, startCol, tableHeaderRow1, endCol);
      worksheet.getCell(tableHeaderRow1, startCol).value = 'TANGGAL PERTEMUAN';
      worksheet.getCell(tableHeaderRow1, startCol).alignment = { horizontal: 'center', vertical: 'middle' };

      recapData.agendas.forEach((a, idx) => {
        const cell = worksheet.getCell(tableHeaderRow2, startCol + idx);
        cell.value = new Date(a.date).toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit' });
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        if (a.status === 'Jam kosong') {
          cell.fill = {
            type: 'pattern',
            pattern: 'darkGrid',
            fgColor: { argb: 'FFFFCCCC' },
            bgColor: { argb: 'FFFFEEEE' }
          };
        }
      });
    }

    const summaryStartCol = 4 + recapData.agendas.length;
    worksheet.mergeCells(tableHeaderRow1, summaryStartCol, tableHeaderRow1, summaryStartCol + 5);
    worksheet.getCell(tableHeaderRow1, summaryStartCol).value = 'JUMLAH';
    worksheet.getCell(tableHeaderRow1, summaryStartCol).alignment = { horizontal: 'center', vertical: 'middle' };

    ['H', 'S', 'I', 'A', 'D', '% HADIR'].forEach((label, idx) => {
      const cell = worksheet.getCell(tableHeaderRow2, summaryStartCol + idx);
      cell.value = label;
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });

    // Apply borders and fill to header
    for (let r = tableHeaderRow1; r <= tableHeaderRow2; r++) {
      for (let c = 1; c <= lastColIndex; c++) {
        const cell = worksheet.getCell(r, c);
        cell.font = { bold: true };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF7C3AED' }
        };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      }
    }

    // Table Data
    currentRow = tableHeaderRow2 + 1;
    recapData.rows.forEach((row, idx) => {
      const rowData = [
        idx + 1,
        row.nisn || row.nis || '',
        row.name.toUpperCase(),
        ...recapData.agendas.map(a => row.history[a.id]?.status ? getStatusCfg(row.history[a.id]!.status).short : '-'),
        row.counts.H,
        row.counts.S,
        row.counts.I,
        row.counts.A,
        row.counts.D,
        row.percentages.H.toFixed(0) + '%'
      ];

      rowData.forEach((data, index) => {
        const cell = worksheet.getCell(currentRow, index + 1);
        cell.value = data;
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
        cell.alignment = { vertical: 'middle' };
        if (index !== 2) { // Center everything except name
          cell.alignment.horizontal = 'center';
        }

        // Apply hatching for Jam Kosong in data rows
        if (index >= 3 && index < 3 + recapData.agendas.length) {
          const agendaIndex = index - 3;
          if (recapData.agendas[agendaIndex].status === 'Jam kosong') {
            cell.fill = {
              type: 'pattern',
              pattern: 'darkGrid',
              fgColor: { argb: 'FFFFCCCC' },
              bgColor: { argb: 'FFFFEEEE' }
            };
          }
        }
      });
      currentRow++;
    });

    currentRow++; // Empty row
    
    // Summary Block
    worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'Persentase Absensi  Kelas';
    worksheet.getCell(`A${currentRow}`).font = { bold: true, color: { argb: 'FF000000' } };
    currentRow++;

    const summaryItems = [
        { label: 'Persentase Absensi', value: recapData.classPercentages.attendance.toFixed(1) + '%' },
        { label: 'Persentase Sakit', value: recapData.classPercentages.sick.toFixed(1) + '%' },
        { label: 'Persentase Izin', value: recapData.classPercentages.permission.toFixed(1) + '%' },
        { label: 'Persentase Alpa', value: recapData.classPercentages.alpa.toFixed(1) + '%' },
        { label: 'Rata-rata', value: recapData.classPercentages.average.toFixed(1) + '%' }
    ];

    summaryItems.forEach(item => {
        worksheet.getCell(`A${currentRow}`).value = item.label;
        worksheet.getCell(`A${currentRow}`).font = { color: { argb: 'FF000000' } };
        worksheet.getCell(`B${currentRow}`).value = ': ' + item.value;
        worksheet.getCell(`B${currentRow}`).font = { color: { argb: 'FF000000' } };
        currentRow++;
    });

    currentRow++; // Empty row
    
    // Signature
    const signatureRow = currentRow + 2;
    const sigCol2 = Math.max(3, lastColIndex - 3); // Towards the right
    
    worksheet.getCell(signatureRow, sigCol2).value = `Kawali, ${today}`;
    worksheet.getCell(signatureRow + 1, sigCol2).value = 'Guru Mata Pelajaran';
    worksheet.getCell(signatureRow + 5, sigCol2).value = activeUser.name;
    worksheet.getCell(signatureRow + 5, sigCol2).font = { bold: true, underline: true };

    // Generate File
    const buffer = await workbook.xlsx.writeBuffer();
    const fileName = `Rekap_Absensi_${activeUser.name.replace(/\s+/g, '_')}_Kelas_${recapClassFilter}_${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(new Blob([buffer]), fileName);
  };

  return (
    <div className="animate-fade-in pb-20">
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          height: 14px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #6366f1;
          border: 3px solid white;
          border-radius: 10px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #4f46e5;
          border: 3px solid #0f172a;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-track {
          background: #1e293b;
        }
      `}</style>
      <div className="flex flex-col sm:flex-row bg-white dark:bg-slate-900 p-2 rounded-[1.5rem] border border-slate-200 dark:border-slate-800 w-full sm:w-fit shadow-sm mb-8">
        <button
          onClick={() => setActiveTab('today')}
          className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${
            activeTab === 'today' 
              ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow-lg shadow-black/10` 
              : 'text-slate-400 hover:text-slate-600'
          }`}
        >
          <BookOpen size={16} /> Agenda Hari Ini
        </button>
        <button
          onClick={() => setActiveTab('pending')}
          className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${
            activeTab === 'pending' 
              ? 'bg-rose-600 text-white shadow-lg shadow-rose-500/20' 
              : 'text-slate-400 hover:text-slate-600'
          }`}
        >
          <AlertCircle size={16} /> Tanggungan Absensi
        </button>
        <button
          onClick={() => setActiveTab('recap')}
          className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${
            activeTab === 'recap' 
              ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow-lg shadow-black/10` 
              : 'text-slate-400 hover:text-slate-600'
          }`}
        >
          <BarChart3 size={16} /> Rekapitulasi Absensi
        </button>
      </div>

      {activeTab === 'today' ? (
        selectedClass && selectedAgendaId ? (
            <StudentAttendanceListMemo 
                agendas={agendas}
                selectedAgendaId={selectedAgendaId}
                selectedClass={selectedClass}
                setSelectedClass={setSelectedClass}
                setSelectedAgendaId={setSelectedAgendaId}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                filteredStudents={filteredStudents}
                attendanceRecords={attendanceRecords}
                handleStatusChange={handleStatusChange}
                handleSaveAllAttendance={handleSaveAllAttendance}
                currentTheme={currentTheme}
            />
        ) : (
            <ClassSelectorMemo 
                agendasToday={agendasToday}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                setSelectedClass={setSelectedClass}
                setSelectedAgendaId={setSelectedAgendaId}
                students={students}
                attendanceRecords={attendanceRecords}
                dayOfWeek={dayOfWeek}
                currentTheme={currentTheme}
            />
        )
      ) : activeTab === 'pending' ? (
        <PendingAttendanceListMemo 
            agendas={agendas}
            schedules={schedules}
            students={students}
            attendanceRecords={attendanceRecords}
            activeUser={activeUser}
            setSelectedDate={setSelectedDate}
            setSelectedClass={setSelectedClass}
            setSelectedAgendaId={setSelectedAgendaId}
            setActiveTab={setActiveTab}
            onNavigate={onNavigate}
            currentTheme={currentTheme}
        />
      ) : (
        <AttendanceRecapMemo 
            activeUser={activeUser}
            recapData={recapData}
            recapClassFilter={recapClassFilter}
            setRecapClassFilter={setRecapClassFilter}
            recapMonth={recapMonth}
            setRecapMonth={setRecapMonth}
            recapYear={recapYear}
            setRecapYear={setRecapYear}
            handlePrintAttendance={handlePrintAttendance}
            handlePrintAllClasses={handlePrintAllClasses}
            handleExportExcel={handleExportExcel}
            setRecapEditPopover={setRecapEditPopover}
            currentTheme={currentTheme}
        />
      )}

      {/* MODAL DISPEN NOTES */}
      {dispenModal.isOpen && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={() => setDispenModal({ ...dispenModal, isOpen: false })}></div>
              <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-[2.5rem] shadow-2xl relative overflow-hidden animate-fade-in-up border border-slate-200 dark:border-slate-800 p-8">
                  <h3 className="text-xl font-black text-slate-800 dark:text-white mb-2">Keterangan Dispen</h3>
                  <p className="text-xs text-slate-500 mb-6">Siswa: <span className="font-black text-indigo-600">{dispenModal.name}</span></p>
                  
                  <textarea 
                    autoFocus
                    placeholder="Contoh: Lomba Olahraga Kabupaten, Mengikuti Seminar OSIS..."
                    className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none font-bold text-sm dark:text-white mb-6 focus:ring-4 focus:ring-purple-500/10"
                    rows={3}
                    value={dispenModal.tempNotes}
                    onChange={(e) => setDispenModal(prev => ({ ...prev, tempNotes: e.target.value }))}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSaveDispenNotes(dispenModal.studentId, dispenModal.tempNotes);
                        }
                    }}
                  />
                  
                  <div className="flex justify-end gap-3">
                      <button onClick={() => setDispenModal({ ...dispenModal, isOpen: false })} className="px-6 py-3 text-xs font-black text-slate-400 uppercase tracking-widest">Batal</button>
                      <button 
                        onClick={() => handleSaveDispenNotes(dispenModal.studentId, dispenModal.tempNotes)}
                        className="px-8 py-3 bg-purple-600 text-white font-black rounded-2xl shadow-lg shadow-purple-500/20 active:scale-95 transition-all text-xs uppercase tracking-widest"
                      >
                          Konfirmasi Dispen
                      </button>
                  </div>
              </div>
          </div>
      )}

      {/* RECAP EDIT POPOVER */}
      {recapEditPopover.isOpen && (
          <>
            <div className="fixed inset-0 z-[120]" onClick={() => setRecapEditPopover({ ...recapEditPopover, isOpen: false })}></div>
            <div 
                className="fixed z-[130] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl p-3 flex flex-col gap-1 animate-fade-in"
                style={{ 
                    left: `${Math.min(recapEditPopover.x, window.innerWidth - 200)}px`, 
                    top: `${Math.min(recapEditPopover.y, window.innerHeight - 300)}px` 
                }}
            >
                <div className="px-2 py-1 mb-2 border-b border-slate-100 dark:border-slate-800">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Edit Absensi</p>
                    <p className="text-[11px] font-black text-slate-800 dark:text-white truncate max-w-[150px]">{recapEditPopover.studentName}</p>
                </div>
                {Object.entries(statusConfig).map(([status, config]) => (
                    <button
                        key={status}
                        onClick={() => handleRecapStatusUpdate(recapEditPopover.studentId, recapEditPopover.agendaId, status as AttendanceStatus)}
                        className={`flex items-center gap-3 px-3 py-2 rounded-xl transition-all hover:bg-slate-50 dark:hover:bg-slate-800 group ${recapEditPopover.currentStatus === status ? 'bg-slate-50 dark:bg-slate-800' : ''}`}
                    >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-xs ${config.color}`}>
                            {config.short}
                        </div>
                        <span className="text-[10px] font-black text-slate-600 dark:text-slate-300 uppercase tracking-widest group-hover:text-indigo-600 transition-colors">
                            {status}
                        </span>
                    </button>
                ))}
            </div>
          </>
      )}
    </div>
  );
};

// --- Sub-components moved outside to prevent flickering/re-mounting ---

const ClassSelectorMemo = React.memo(({ 
    agendasToday, 
    selectedDate, 
    setSelectedDate, 
    setSelectedClass, 
    setSelectedAgendaId, 
    students, 
    attendanceRecords, 
    dayOfWeek 
}: any) => {
    return (
        <div className="animate-fade-in-up">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-10 gap-6">
                <div>
                    <h2 className="text-3xl font-black text-slate-800 dark:text-white tracking-tight leading-none mb-2">Pilih Agenda Mengajar</h2>
                    <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest flex items-center gap-2">
                        <Clock size={12} className="text-indigo-500" />
                        {dayOfWeek}, {safeFormatDate(selectedDate, { day: '2-digit', month: 'long', year: 'numeric' })}
                    </p>
                </div>
                
                <div className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm focus-within:ring-4 focus-within:ring-indigo-500/10 transition-all cursor-pointer" onClick={(e) => {
                    try {
                        const input = e.currentTarget.querySelector('input[type="date"]');
                        if (input) (input as HTMLInputElement).showPicker();
                    } catch (err) {}
                }}>
                    <Calendar size={18} className="text-indigo-500" />
                    <input 
                        type="date" 
                        value={selectedDate}
                        onChange={e => setSelectedDate(e.target.value)}
                        onClick={(e) => {
                            e.stopPropagation();
                            try {
                                e.currentTarget.showPicker();
                            } catch (err) {}
                        }}
                        className="bg-transparent border-none text-xs font-black text-slate-700 dark:text-white focus:ring-0 outline-none cursor-pointer"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                {agendasToday.length > 0 ? agendasToday.map((agenda: any) => {
                    const studentCount = students.filter(s => s.class === agenda.class && s.status === 'Aktif').length;
                    const records = attendanceRecords.filter(r => r.agendaId === agenda.id);
                    const isCompleted = records.length >= studentCount && studentCount > 0;
                    const isPartiallyFilled = records.length > 0 && !isCompleted;
                    const isJamKosong = agenda.status === 'Jam kosong';

                    return (
                    <button 
                        key={agenda.id}
                        disabled={isJamKosong}
                        onClick={() => { setSelectedClass(agenda.class); setSelectedAgendaId(agenda.id); }}
                        className={`p-8 bg-white dark:bg-slate-900 border-2 rounded-[2.5rem] transition-all flex flex-col gap-6 group text-left relative overflow-hidden
                            ${isJamKosong ? 'opacity-60 border-slate-100 dark:border-slate-800 cursor-not-allowed bg-slate-50/50 dark:bg-slate-800/10' : 
                              isCompleted ? 'border-emerald-100 dark:border-emerald-900/30 hover:border-emerald-500 hover:shadow-2xl' : 
                              isPartiallyFilled ? 'border-amber-100 dark:border-amber-900/30 hover:border-amber-500 hover:shadow-2xl' :
                              'border-slate-100 dark:border-slate-800 hover:border-indigo-500 hover:shadow-2xl'}`}
                    >
                        {isJamKosong && (
                            <div className="absolute inset-0 bg-hatch-red opacity-10 pointer-events-none"></div>
                        )}
                        
                        <div className="flex justify-between items-start">
                            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center font-black text-2xl shadow-lg transition-all group-hover:scale-110
                                ${isJamKosong ? 'bg-slate-200 dark:bg-slate-700 text-slate-500' :
                                  isCompleted ? 'bg-emerald-600 text-white shadow-emerald-500/20' : 
                                  isPartiallyFilled ? 'bg-amber-500 text-white shadow-amber-500/20' :
                                  'bg-indigo-600 text-white shadow-indigo-500/20'}`}>
                                {agenda.class}
                            </div>
                            
                            <div className="flex flex-col items-end gap-2">
                                {isJamKosong ? (
                                    <span className="px-3 py-1 bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-400 rounded-xl text-[9px] font-black uppercase tracking-widest border border-rose-200 dark:border-rose-900/30">
                                        JAM KOSONG
                                    </span>
                                ) : isCompleted ? (
                                    <div className="flex items-center gap-1 text-emerald-500 animate-pulse">
                                        <CheckCircle2 size={16} />
                                        <span className="text-[9px] font-black uppercase tracking-widest">Selesai</span>
                                    </div>
                                ) : (
                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                                        {records.length}/{studentCount} Siswa
                                    </span>
                                )}
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded-lg text-[8px] font-black text-slate-500 uppercase tracking-tighter">
                                    JAM {agenda.startPeriod === agenda.endPeriod ? agenda.startPeriod : `${agenda.startPeriod}-${agenda.endPeriod}`}
                                </span>
                                <span className="text-[9px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest">{agenda.activityType}</span>
                            </div>
                            <h3 className="font-black text-slate-800 dark:text-white text-lg tracking-tight group-hover:text-indigo-600 transition-colors">{agenda.topic}</h3>
                        </div>

                        {!isJamKosong && (
                            <div className="mt-auto flex items-center justify-between pt-6 border-t border-slate-50 dark:border-slate-800">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest group-hover:translate-x-1 transition-transform inline-flex items-center gap-2">
                                    Isi Absensi <ArrowRight size={12} />
                                </span>
                                <Users size={18} className="text-slate-200 group-hover:text-indigo-200 transition-colors" />
                            </div>
                        )}
                    </button>
                );
                }) : (
                    <div className="col-span-full py-24 text-center bg-slate-50/50 dark:bg-slate-800/10 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-[3rem] animate-pulse">
                        <Calendar className="mx-auto mb-4 text-slate-200" size={64} />
                        <p className="text-slate-400 font-bold italic">Tidak ada agenda mengajar pada tanggal ini.</p>
                        <p className="text-xs text-slate-400 mt-2">Silakan pilih tanggal lain atau cek jadwal mengajar Anda.</p>
                    </div>
                )}
            </div>
        </div>
    );
});

const StudentAttendanceListMemo = React.memo(({ 
    agendas, 
    selectedAgendaId, 
    selectedClass, 
    setSelectedClass, 
    setSelectedAgendaId, 
    searchTerm, 
    setSearchTerm, 
    filteredStudents, 
    attendanceRecords, 
    handleStatusChange, 
    handleSaveAllAttendance,
    currentTheme
}: any) => {
    const selectedAgenda = agendas.find(a => a.id === selectedAgendaId);
    
    return (
    <div className="animate-fade-in-up">
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center mb-10 gap-6">
        <div className="flex items-center gap-4">
            <button onClick={() => { setSelectedClass(null); setSelectedAgendaId(null); }} className="p-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-2xl hover:bg-indigo-600 hover:text-white transition-all shadow-sm">
                <ChevronLeft size={20} />
            </button>
            <div>
                <h2 className="text-2xl font-black text-slate-800 dark:text-white tracking-tight leading-none mb-1">Absensi Kelas {selectedClass}</h2>
                <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-1">
                    {selectedAgenda ? (
                        <>
                            {new Date(selectedAgenda.date).toLocaleDateString('id-ID', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })} • Jam {selectedAgenda.startPeriod === 0 ? 'Tambahan' : `${selectedAgenda.startPeriod}-${selectedAgenda.endPeriod}`} • {selectedAgenda.topic}
                        </>
                    ) : 'Memuat agenda...'}
                </p>
            </div>
        </div>
        
        <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input 
                type="text" 
                placeholder="Cari siswa..." 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-4 focus:ring-indigo-500/10 text-xs font-bold dark:text-white"
            />
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full table-fixed text-sm text-left border-separate border-spacing-y-2">
          <thead>
            <tr className="text-[10px] text-slate-400 uppercase tracking-widest font-black">
              <th className="px-4 py-4 w-[8%] text-center">No</th>
              <th className="px-6 py-4 w-[38%]">Siswa / NISN</th>
              <th className="px-6 py-4 w-[54%] text-center">Status Kehadiran</th>
            </tr>
          </thead>
          <tbody>
            {filteredStudents.map((student: any, idx: number) => {
              const record = attendanceRecords.find((r: any) => r.agendaId === selectedAgendaId && r.studentId === student.id);
              const currentStatus = record?.status || AttendanceStatus.Hadir;
              const currentDescription = record?.description;
              
              return (
              <tr key={student.id} className="group transition-all">
                <td className="px-4 py-5 bg-slate-50/50 dark:bg-slate-800/30 rounded-l-3xl border-y border-l border-slate-100 dark:border-slate-800 text-center font-black text-xs text-slate-500 dark:text-slate-400">
                  {idx + 1}
                </td>
                <td className="px-6 py-5 bg-slate-50/50 dark:bg-slate-800/30 border-y border-slate-100 dark:border-slate-800">
                  <p className={`font-black mb-0.5 ${getStudentNameColor(student.gender)}`}>{idx + 1}. {student.name}</p>
                  <p className="text-[10px] font-bold text-slate-400">NISN: {student.nisn || student.nis || '-'}</p>
                  {currentDescription && (
                      <div className="mt-2 flex items-center gap-1.5 text-[9px] font-black text-purple-600 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded-lg w-fit">
                          <MessageSquare size={10} />
                          Keterangan: {currentDescription}
                      </div>
                  )}
                </td>
                <td className="px-6 py-5 bg-slate-50/50 dark:bg-slate-800/30 rounded-r-3xl border-y border-r border-slate-100 dark:border-slate-800">
                  <div className="flex items-center justify-center gap-3">
                    {Object.values(AttendanceStatus).map(status => {
                      const isActive = currentStatus === status;
                      return (
                        <button 
                          key={status}
                          onClick={() => handleStatusChange(student.id, status)}
                          className={`w-10 h-10 rounded-2xl font-black text-xs transition-all flex items-center justify-center shadow-sm
                            ${isActive ? `${getStatusCfg(status).activeBg} text-white border-transparent` : 'bg-white dark:bg-slate-900 text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-indigo-500'}`}
                          title={status}
                        >
                          {getStatusCfg(status).short}
                        </button>
                      );
                    })}
                  </div>
                </td>
              </tr>
            )})}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {filteredStudents.map((student: any, idx: number) => {
          const record = attendanceRecords.find((r: any) => r.agendaId === selectedAgendaId && r.studentId === student.id);
          const currentStatus = record?.status || AttendanceStatus.Hadir;
          const currentDescription = record?.description;
          
          return (
            <div key={student.id} className="bg-white dark:bg-slate-900 p-5 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col gap-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-black text-xs flex items-center justify-center shrink-0 border border-slate-200 dark:border-slate-700">
                    {idx + 1}
                  </span>
                  <div>
                    <p className={`font-black text-sm ${getStudentNameColor(student.gender)}`}>{idx + 1}. {student.name}</p>
                    <p className="text-[10px] font-bold text-slate-400">NISN: {student.nisn || student.nis || '-'}</p>
                  </div>
                </div>
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-black text-xs ${getStatusCfg(currentStatus).color}`}>
                  {getStatusCfg(currentStatus).short}
                </div>
              </div>

              {currentDescription && (
                <div className="flex items-center gap-1.5 text-[9px] font-black text-purple-600 bg-purple-50 dark:bg-purple-900/20 px-3 py-2 rounded-xl">
                  <MessageSquare size={12} />
                  Keterangan: {currentDescription}
                </div>
              )}

              <div className="grid grid-cols-5 gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                {Object.values(AttendanceStatus).map(status => {
                  const isActive = currentStatus === status;
                  const cfg = getStatusCfg(status);
                  return (
                    <button 
                      key={status}
                      onClick={() => handleStatusChange(student.id, status)}
                      className={`py-3 rounded-xl font-black text-[10px] transition-all flex flex-col items-center justify-center gap-1
                        ${isActive ? `${cfg.activeBg} text-white` : 'bg-slate-50 dark:bg-slate-800 text-slate-400 border border-slate-100 dark:border-slate-700'}`}
                    >
                      <span>{cfg.short}</span>
                      <span className="text-[7px] uppercase tracking-tighter opacity-70">{cfg.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-col sm:flex-row justify-end mt-10 gap-4 items-center">
        <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest hidden sm:block">Perubahan Tersimpan Otomatis</p>
        <div className="flex gap-4 w-full sm:w-auto">
            <button onClick={() => { setSelectedClass(null); setSelectedAgendaId(null); }} className="flex-1 sm:flex-none px-10 py-4 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-black rounded-3xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-all uppercase tracking-widest text-xs">Kembali</button>
            <button onClick={handleSaveAllAttendance} className={`flex-1 sm:flex-none px-10 py-4 bg-gradient-to-r ${currentTheme?.activeNavGradient || currentTheme?.accentGradient || 'from-indigo-600 to-violet-600'} text-white font-black rounded-3xl shadow-xl shadow-black/10 active:scale-95 transition-all uppercase tracking-widest text-xs`}>Selesaikan Absensi</button>
        </div>
      </div>
    </div>
    );
});

const AttendanceRecapMemo = React.memo(({ 
    activeUser, 
    recapData, 
    recapClassFilter, 
    setRecapClassFilter, 
    recapMonth, 
    setRecapMonth, 
    recapYear, 
    setRecapYear, 
    handlePrintAttendance, 
    handlePrintAllClasses, 
    handleExportExcel, 
    setRecapEditPopover,
    currentTheme
}: any) => {
    const classOptions = useMemo(() => {
        return activeUser.assignedClasses.sort();
    }, [activeUser]);

    const handlePrevClass = () => {
        if (!classOptions || classOptions.length <= 1) return;
        const currentIndex = classOptions.indexOf(recapClassFilter);
        if (currentIndex === -1) return;
        const prevIndex = (currentIndex - 1 + classOptions.length) % classOptions.length;
        setRecapClassFilter(classOptions[prevIndex]);
    };

    const handleNextClass = () => {
        if (!classOptions || classOptions.length <= 1) return;
        const currentIndex = classOptions.indexOf(recapClassFilter);
        if (currentIndex === -1) return;
        const nextIndex = (currentIndex + 1) % classOptions.length;
        setRecapClassFilter(classOptions[nextIndex]);
    };

    const currentAgendas = recapData.agendas;
    const currentStudents = recapData.rows;

    return (
        <div className="animate-fade-in-up">
            <div className="flex flex-col xl:flex-row justify-between items-start xl:items-end mb-12 gap-8">
                <div className="relative">
                    <span className="absolute -top-4 left-0 text-[10px] font-black text-indigo-600 uppercase tracking-[0.3em]">Laporan Akademik</span>
                    <h2 className="text-4xl xl:text-5xl font-black text-slate-800 dark:text-white tracking-tighter leading-none">
                        Rekapitulasi <span className="text-indigo-600">Absensi</span>
                    </h2>
                    <div className="flex items-center gap-3 mt-4">
                        <span className="px-3 py-1 bg-slate-100 dark:bg-slate-800 rounded-full text-[10px] font-black text-slate-500 uppercase tracking-widest border border-slate-200 dark:border-slate-700">
                            Kelas {recapClassFilter}
                        </span>
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-300"></span>
                        <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest">
                             Status Kehadiran Siswa per Pertemuan
                        </p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 bg-white dark:bg-slate-900 p-3 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-md">
                    <div className="flex items-center gap-1 bg-indigo-50/50 dark:bg-indigo-900/20 rounded-2xl border border-indigo-100 dark:border-indigo-900/30 hover:border-indigo-300 transition-all p-1">
                        {classOptions.length > 1 && (
                            <button
                                type="button"
                                onClick={handlePrevClass}
                                className="p-1.5 hover:bg-white dark:hover:bg-slate-800 text-indigo-600 dark:text-indigo-400 rounded-xl transition-all cursor-pointer"
                                title="Kelas Sebelumnya"
                            >
                                <ChevronLeft size={16} />
                            </button>
                        )}
                        
                        <div className="flex items-center gap-1.5 px-2 py-1">
                            <label className="text-[10px] font-black text-indigo-400 dark:text-indigo-500 uppercase tracking-widest">Kelas</label>
                            <select
                                value={recapClassFilter}
                                onChange={e => setRecapClassFilter(e.target.value)}
                                className="bg-transparent border-none text-sm font-black text-indigo-900 dark:text-indigo-100 focus:ring-0 cursor-pointer outline-none min-w-[60px]"
                            >
                                {classOptions.map((cls: any) => (
                                    <option key={cls} value={cls} className="bg-white dark:bg-slate-900 text-slate-800 dark:text-white">{cls}</option>
                                ))}
                            </select>
                        </div>

                        {classOptions.length > 1 && (
                            <button
                                type="button"
                                onClick={handleNextClass}
                                className="p-1.5 hover:bg-white dark:hover:bg-slate-800 text-indigo-600 dark:text-indigo-400 rounded-xl transition-all cursor-pointer"
                                title="Kelas Berikutnya"
                            >
                                <ChevronRight size={16} />
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50/50 dark:bg-emerald-900/20 rounded-2xl border border-emerald-100 dark:border-emerald-900/30 hover:border-emerald-300 transition-all group">
                        <label className="text-[10px] font-black text-emerald-400 dark:text-emerald-500 uppercase tracking-widest">Bulan</label>
                        <select
                            value={recapMonth}
                            onChange={e => setRecapMonth(e.target.value)}
                            className="bg-transparent border-none text-sm font-black text-emerald-900 dark:text-emerald-100 focus:ring-0 cursor-pointer outline-none min-w-[100px]"
                        >
                            <option value="SEMUA" className="bg-white dark:bg-slate-900 text-slate-800 dark:text-white">Semua Bulan</option>
                            {monthNames.map((name, idx) => (
                                <option key={name} value={(idx + 1).toString()} className="bg-white dark:bg-slate-900 text-slate-800 dark:text-white">{name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-2 px-4 py-2 bg-amber-50/50 dark:bg-amber-900/20 rounded-2xl border border-amber-100 dark:border-amber-900/30 hover:border-amber-300 transition-all group">
                        <label className="text-[10px] font-black text-amber-400 dark:text-amber-500 uppercase tracking-widest">Tahun</label>
                        <select
                            value={recapYear}
                            onChange={e => setRecapYear(e.target.value)}
                            className="bg-transparent border-none text-sm font-black text-amber-900 dark:text-amber-100 focus:ring-0 cursor-pointer outline-none min-w-[80px]"
                        >
                            <option value="SEMUA" className="bg-white dark:bg-slate-900 text-slate-800 dark:text-white">Semua</option>
                            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                                <option key={year} value={year.toString()} className="bg-white dark:bg-slate-900 text-slate-800 dark:text-white">{year}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-2">
                        <button onClick={handlePrintAttendance} className="p-3 bg-slate-100 dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 rounded-2xl hover:bg-indigo-600 hover:text-white transition-all shadow-sm" title="Cetak Laporan">
                            <Printer size={20} />
                        </button>
                        <button onClick={handlePrintAllClasses} className="px-4 py-3 bg-indigo-600 text-white rounded-2xl hover:bg-indigo-700 transition-all shadow-sm flex items-center gap-2" title="Cetak Semua Kelas">
                           <Printer size={16} /> <span className="text-[10px] font-black uppercase tracking-widest hidden lg:inline">Cetak Semua</span>
                        </button>
                        <button onClick={handleExportExcel} className="p-3 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-2xl hover:bg-emerald-600 hover:text-white transition-all shadow-sm" title="Export Excel">
                            <FileSpreadsheet size={20} />
                        </button>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
                <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 p-5 rounded-[2rem] shadow-sm">
                    <p className="text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest mb-1 leading-tight">Persentase Absensi</p>
                    <h4 className="text-2xl font-black text-emerald-700 dark:text-emerald-300">{recapData.classPercentages.attendance.toFixed(1)}%</h4>
                </div>
                <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-500/20 p-5 rounded-[2rem] shadow-sm">
                    <p className="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-1 leading-tight">Persentase Izin</p>
                    <h4 className="text-2xl font-black text-blue-700 dark:text-blue-300">{recapData.classPercentages.permission.toFixed(1)}%</h4>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-100 dark:border-yellow-500/20 p-5 rounded-[2rem] shadow-sm">
                    <p className="text-[10px] font-black text-yellow-600 dark:text-yellow-400 uppercase tracking-widest mb-1 leading-tight">Persentase Sakit</p>
                    <h4 className="text-2xl font-black text-yellow-700 dark:text-yellow-300">{recapData.classPercentages.sick.toFixed(1)}%</h4>
                </div>
                <div className="bg-rose-50 dark:bg-rose-500/10 border border-rose-100 dark:border-rose-500/20 p-5 rounded-[2rem] shadow-sm">
                    <p className="text-[10px] font-black text-rose-600 dark:text-rose-400 uppercase tracking-widest mb-1 leading-tight">Persentase Alpa</p>
                    <h4 className="text-2xl font-black text-rose-700 dark:text-rose-300">{recapData.classPercentages.alpa.toFixed(1)}%</h4>
                </div>
                <div className="bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 p-5 rounded-[2rem] shadow-sm col-span-2 md:col-span-1">
                    <p className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-1 leading-tight">Rata-rata</p>
                    <h4 className="text-2xl font-black text-indigo-700 dark:text-indigo-300">{recapData.classPercentages.average.toFixed(1)}%</h4>
                </div>
            </div>

            <div className="relative">
                <div className="absolute -top-7 right-4 hidden md:flex items-center gap-2 text-[10px] font-black text-indigo-500/60 uppercase tracking-[0.2em] animate-pulse">
                    <ArrowRight size={10} className="rotate-180" />
                    <span>Geser Tabel</span>
                    <ArrowRight size={10} />
                </div>

                <div 
                    className="overflow-x-auto custom-scrollbar rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 mb-12"
                    style={{ transform: 'rotateX(180deg)' }}
                >
                    <table className="w-full border-collapse" style={{ transform: 'rotateX(180deg)' }}>
                        <thead>
                            <tr className="bg-slate-50/50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                                <th className="sticky left-0 z-20 bg-slate-50 dark:bg-slate-800 px-6 py-4 text-center border-r border-slate-200 dark:border-slate-700 w-16">
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">No</span>
                                </th>
                                <th className="sticky left-16 z-20 bg-slate-50 dark:bg-slate-800 px-6 py-4 text-left border-r border-slate-200 dark:border-slate-700 min-w-[240px]">
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Nama Peserta Didik</span>
                                </th>
                                
                                {currentAgendas.map(agenda => (
                                    <th key={agenda.id} className="px-4 py-4 text-center min-w-[90px] border-r border-slate-200 dark:border-slate-700">
                                        <div className="flex flex-col items-center gap-0.5">
                                            <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest ${agenda.status === 'Jam kosong' ? 'bg-rose-100 text-rose-600' : 'bg-indigo-100 text-indigo-600'}`}>
                                                {new Date(agenda.date).toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit' })}
                                            </span>
                                            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-tighter">JAM {agenda.startPeriod}-{agenda.endPeriod}</span>
                                        </div>
                                    </th>
                                ))}

                                <th className="sticky right-0 z-20 px-6 py-4 text-center bg-indigo-50 dark:bg-indigo-950 border-l border-slate-200 dark:border-slate-700 min-w-[240px] shadow-[-10px_0_15px_rgba(0,0,0,0.05)]">
                                    <div className="flex items-center justify-center gap-4">
                                        <span className="text-xs font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest">Rekap Kumulatif & %</span>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentStudents.map((row: any, idx: number) => (
                                <tr key={row.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 transition-colors group">
                                    <td className="sticky left-0 z-10 bg-white dark:bg-slate-900 group-hover:bg-slate-50 dark:group-hover:bg-slate-800 px-6 py-2.5 text-center font-black text-slate-400 border-r border-slate-200 dark:border-slate-700">
                                        {idx + 1}
                                    </td>
                                    <td className="sticky left-16 z-10 bg-white dark:bg-slate-900 group-hover:bg-slate-50 dark:group-hover:bg-slate-800 px-6 py-2.5 text-left font-black border-r border-slate-200 dark:border-slate-700">
                                        <p className={`text-xs uppercase tracking-tight truncate max-w-[220px] ${getStudentNameColor(row.gender)}`}>{row.name}</p>
                                        <p className="text-[8px] font-bold text-slate-400 uppercase mt-0.5">NISN: {row.nisn || row.nis || '-'}</p>
                                    </td>

                                    {currentAgendas.map(agenda => {
                                        const statusInfo = row.history[agenda.id];
                                        const status = statusInfo?.status || null;
                                        const config = status ? statusConfig[status] : null;

                                        return (
                                            <td key={`${row.id}-${agenda.id}`} className="px-3 py-2.5 border-r border-slate-100 dark:border-slate-800">
                                                <div className="flex justify-center">
                                                    <button 
                                                        disabled={agenda.status === 'Jam kosong'}
                                                        onClick={(e) => {
                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                            setRecapEditPopover({
                                                                isOpen: true,
                                                                studentId: row.id,
                                                                agendaId: agenda.id,
                                                                studentName: row.name,
                                                                currentStatus: status,
                                                                x: rect.left + window.scrollX,
                                                                y: rect.top + window.scrollY + rect.height
                                                            });
                                                        }}
                                                        className={`w-7 h-7 rounded-lg flex items-center justify-center font-black text-[10px] transition-all shadow-sm
                                                            ${agenda.status === 'Jam kosong' ? 'bg-hatch-red border-rose-200 cursor-not-allowed opacity-40' : 'hover:scale-110 active:scale-95'}
                                                            ${status ? `${config.activeBg} border-transparent` : (agenda.status === 'Jam kosong' ? '' : 'bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-300')}`}
                                                        title={statusInfo?.description || ''}
                                                    >
                                                        {status ? config.short : '-'}
                                                    </button>
                                                </div>
                                            </td>
                                        );
                                    })}

                                    <td className="sticky right-0 z-10 px-6 py-2.5 bg-indigo-50 dark:bg-slate-900 group-hover:bg-indigo-100 dark:group-hover:bg-slate-800 border-l border-slate-200 dark:border-slate-700 shadow-[-10px_0_15px_rgba(0,0,0,0.05)]">
                                        <div className="flex items-center justify-center gap-3">
                                            {Object.entries(row.counts).map(([type, count]) => (
                                                <div key={type} className="flex flex-col items-center min-w-[28px]">
                                                    <span className={`text-xs font-black ${type === 'H' ? 'text-green-600' : type === 'A' ? 'text-rose-600' : 'text-slate-500'}`}>
                                                        {count as number}
                                                    </span>
                                                    <span className="text-[8px] font-black opacity-40 uppercase">{type}</span>
                                                </div>
                                            ))}
                                            <div className="h-6 w-[1.5px] bg-indigo-200 dark:bg-indigo-800 mx-1"></div>
                                            <div className="flex flex-col items-center px-1">
                                                <span className="text-[11px] font-black text-indigo-600 dark:text-indigo-400">
                                                    {row.percentages.H.toFixed(0)}%
                                                </span>
                                                <span className="text-[7px] font-black opacity-40 uppercase">Presensi</span>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Mobile View for Recap */}
            <div className="md:hidden space-y-6 mb-12">
                <h3 className="flex items-center gap-2 text-xs font-black text-slate-400 uppercase tracking-[0.2em] px-2 mb-4">
                    <Users size={14} className="text-indigo-500" />
                    Daftar Absensi Mobile
                </h3>
                <div className="grid gap-4">
                    {recapData.rows.map((row: any) => {
                        const totalAttend = Object.values(row.counts).reduce((a: any, b: any) => a + (b as number), 0);
                        return (
                            <div key={`${row.id}-card`} className="bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 p-6 shadow-sm overflow-hidden">
                                <div className="flex justify-between items-start mb-6">
                                    <div>
                                        <h4 className={`font-black uppercase tracking-tight ${getStudentNameColor(row.gender)}`}>{row.name}</h4>
                                        <p className="text-[9px] font-bold text-slate-400 uppercase mt-0.5">NISN: {row.nisn || row.nis || '-'}</p>
                                    </div>
                                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl border border-indigo-100 dark:border-indigo-900/30">
                                        <span className="text-[9px] font-black text-indigo-600 uppercase">H: {row.counts.H}</span>
                                        <span className="w-1 h-1 bg-indigo-200 rounded-full"></span>
                                        <span className="text-[9px] font-black text-rose-600 uppercase">A: {row.counts.A}</span>
                                    </div>
                                </div>
                                
                                <div className="space-y-4">
                                    <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-3xl border border-slate-100 dark:border-slate-700">
                                        <div className="flex justify-between items-center mb-4">
                                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Riwayat 10 Terakhir</span>
                                            <span className="text-[9px] font-bold text-slate-400 italic">Terbaru di kanan</span>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {currentAgendas.map((agenda: any) => {
                                                const statusInfo = row.history[agenda.id];
                                                const status = statusInfo?.status || null;
                                                const config = status ? getStatusCfg(status) : null;
                                                
                                                return (
                                                    <div 
                                                        key={`${row.id}-${agenda.id}-mobile`}
                                                        className={`flex flex-col items-center justify-center w-9 h-11 rounded-xl border text-[10px] font-black transition-all shadow-sm ${agenda.status === 'Jam kosong' ? 'bg-hatch-red border-rose-200 opacity-40 cursor-not-allowed' : 'cursor-pointer active:scale-90'} ${status ? `${config.color} border-transparent` : 'bg-slate-50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-700 text-slate-300'}`}
                                                        title={statusInfo?.description || ''}
                                                        onClick={(e) => {
                                                            if (agenda.status === 'Jam kosong') return;
                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                            setRecapEditPopover({
                                                                isOpen: true,
                                                                studentId: row.id,
                                                                agendaId: agenda.id,
                                                                studentName: row.name,
                                                                currentStatus: status,
                                                                x: rect.left + window.scrollX,
                                                                y: rect.top + window.scrollY + rect.height
                                                            });
                                                        }}
                                                    >
                                                        <span className="text-[7px] opacity-60 mb-0.5">
                                                            {new Date(agenda.date).getDate()}/{new Date(agenda.date).getMonth() + 1}
                                                        </span>
                                                        <span className="text-xs">{status ? config.short : '-'}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {recapData.rows.length === 0 && (
                <div className="py-20 text-center bg-slate-50/50 dark:bg-slate-800/30 rounded-[3rem] border-2 border-dashed border-slate-200 dark:border-slate-800">
                    <BarChart3 className="mx-auto mb-4 text-slate-200" size={64} />
                    <p className="text-slate-400 font-bold italic">Tidak ada data absensi untuk kelas {recapClassFilter}.</p>
                    <p className="text-xs text-slate-400 mt-2">Pastikan ada agenda dan absensi sudah diisi untuk kelas ini.</p>
                </div>
            )}
        </div>
    );
});

const PendingAttendanceListMemo = React.memo(({ 
    agendas, 
    schedules,
    students, 
    attendanceRecords, 
    activeUser, 
    setSelectedDate, 
    setSelectedClass, 
    setSelectedAgendaId, 
    setActiveTab,
    onNavigate
}: any) => {
    const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
    const [filterClass, setFilterClass] = useState<string>('SEMUA');

    const pendingData = useMemo(() => {
      const list: any[] = [];
      const teacherSubject = (activeUser?.subject || '').trim().toLowerCase();
      const teacherClasses = (activeUser?.assignedClasses || []).map((c: string) => c.trim().toLowerCase());

      // Filter agendas to only those in the teacher's recap agenda
      const teacherAgendas = agendas.filter(a => {
        if (a.isArchived) return false;
        
        const agTeacherId = a.teacherId;
        const agSubject = (a.subject || '').trim().toLowerCase();
        const agClass = (a.class || '').trim().toLowerCase();
        
        if (agTeacherId && activeUser?.id) {
          return agTeacherId === activeUser.id;
        }
        
        // Fallback for legacy agendas without teacherId
        const isSubjectMatch = teacherSubject && agSubject && agSubject === teacherSubject;
        const isClassMatch = teacherClasses.length > 0 && agClass && (teacherClasses.includes(agClass) || agClass === 'semua kelas');
        return isSubjectMatch && isClassMatch;
      });

      // 1. Existing incomplete agendas
      teacherAgendas.forEach(a => {
        const agClass = (a.class || '').trim().toLowerCase();
        if (filterClass !== 'SEMUA' && a.class !== filterClass) return;
        
        const isExtraAgendaWithoutClass = (a.activityType === ActivityType.SUMATIF || a.activityType === ActivityType.SAS || (a.activityType as any) === 'SAS' || (a.activityType as any) === 'Sumatif Akhir') && (!a.class || a.class === '');
        const isJamKosong = a.status === 'Jam kosong';
        const isNoAbsenceActivity = [
          ActivityType.KEGIATAN_LOMBA, 
          ActivityType.KEGIATAN_KESISWAAN, 
          ActivityType.KEGIATAN_SEKOLAH, 
          ActivityType.SAS
        ].includes(a.activityType as any) || 
        (a.activityType as any) === 'SAS' || 
        (a.activityType as any) === 'Sumatif Akhir' ||
        a.isAttendanceOptional;
        
        if (isExtraAgendaWithoutClass || isJamKosong || isNoAbsenceActivity) return;

        const count = students.filter(s => {
            const sClass = (s.class || '').trim().toLowerCase();
            return sClass === agClass && s.status === 'Aktif';
        }).length;
        const recordCount = attendanceRecords.filter(r => r.agendaId === a.id).length;
        
        // Show if attendance is not complete
        if (recordCount < count && count > 0) {
          list.push({
             type: 'incomplete_agenda',
             id: a.id,
             agenda: a,
             count,
             recordCount,
             date: a.date,
             class: a.class
          });
        }
      });

      return list.sort((a, b) => {
        const timeA = new Date(a.date).getTime();
        const timeB = new Date(b.date).getTime();
        return sortOrder === 'newest' ? timeB - timeA : timeA - timeB;
      });
    }, [agendas, schedules, students, attendanceRecords, activeUser, sortOrder, filterClass]);

    return (
      <div className="animate-fade-in-up">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
            <div>
                <h2 className="text-2xl font-black text-slate-800 dark:text-white tracking-tight leading-none mb-1">Tanggungan Absensi</h2>
                <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-1">
                    Daftar agenda yang data absensinya belum lengkap atau belum diisi.
                </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
                <select
                    value={filterClass}
                    onChange={e => setFilterClass(e.target.value)}
                    className="px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold dark:text-white outline-none focus:ring-2 focus:ring-indigo-500/20"
                >
                    <option value="SEMUA">Semua Kelas</option>
                    {[...activeUser.assignedClasses].sort().map(cls => (
                        <option key={cls} value={cls}>{cls}</option>
                    ))}
                </select>
                <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 p-1.5 rounded-2xl border border-slate-200 dark:border-slate-700">
                    <button 
                        onClick={() => setSortOrder('newest')}
                        className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${sortOrder === 'newest' ? 'bg-white dark:bg-slate-900 text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        Terbaru
                    </button>
                    <button 
                        onClick={() => setSortOrder('oldest')}
                        className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${sortOrder === 'oldest' ? 'bg-white dark:bg-slate-900 text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                        Terlama
                    </button>
                </div>
            </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {pendingData.length > 0 ? pendingData.map(item => {
                if (item.type === 'incomplete_agenda') {
                  const agenda = item.agenda;
                  return (
                    <button 
                        key={item.id}
                        onClick={() => { 
                            setSelectedDate(agenda.date);
                            setSelectedClass(agenda.class); 
                            setSelectedAgendaId(agenda.id); 
                            setActiveTab('today'); 
                        }}
                        className="p-6 bg-white dark:bg-slate-900 border-2 rounded-[2rem] hover:border-rose-500 hover:shadow-2xl transition-all flex flex-col gap-4 group text-left border-rose-100 dark:border-rose-900/30"
                    >
                        <div className="flex justify-between items-start">
                            <div className="w-12 h-12 bg-rose-600 rounded-xl flex items-center justify-center text-white font-black text-xl shadow-lg shadow-rose-500/20">
                                {agenda.class}
                            </div>
                            <span className="px-2 py-1 bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400 rounded-lg text-[9px] font-black uppercase tracking-widest">
                                {item.recordCount === 0 ? 'BELUM ABSEN' : `KURANG ${item.count - item.recordCount} SISWA`}
                            </span>
                        </div>
                        <div>
                            <p className="font-black text-slate-800 dark:text-white text-sm uppercase tracking-tight line-clamp-1">{agenda.topic}</p>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                                {new Date(agenda.date).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })} • Jam {agenda.startPeriod}-{agenda.endPeriod}
                            </p>
                        </div>
                    </button>
                  );
                } else {
                  const s = item.schedule;
                  return (
                    <button 
                        key={item.id}
                        onClick={() => { 
                            if (onNavigate) {
                              onNavigate(View.Agenda, { date: item.date, class: s.class, period: s.period });
                            }
                        }}
                        className="p-6 bg-amber-50 dark:bg-amber-900/10 border-2 border-dashed rounded-[2rem] hover:border-amber-500 hover:shadow-2xl transition-all flex flex-col gap-4 group text-left border-amber-200 dark:border-amber-800/50"
                    >
                        <div className="flex justify-between items-start">
                            <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center text-white font-black text-xl shadow-lg shadow-amber-500/20">
                                {s.class}
                            </div>
                            <span className="px-2 py-1 bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400 rounded-lg text-[9px] font-black uppercase tracking-widest">
                                AGENDA BELUM DIBUAT
                            </span>
                        </div>
                        <div>
                            <p className="font-black text-slate-800 dark:text-white text-sm uppercase tracking-tight">Jadwal: {s.subject}</p>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                                Jam ke-{s.period} • {new Date(item.date).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </p>
                            <p className="text-[9px] font-black text-amber-600 dark:text-amber-400 mt-2 uppercase tracking-widest">Klik untuk buat agenda & absensi</p>
                        </div>
                    </button>
                  );
                }
            }) : (
                <div className="col-span-full py-20 text-center bg-slate-50 dark:bg-slate-800/30 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-[2.5rem]">
                    <CheckCircle2 className="mx-auto mb-4 text-green-400" size={64} />
                    <p className="text-slate-400 font-bold italic">Luar biasa! Tidak ada tanggungan absensi.</p>
                    <p className="text-xs text-slate-400 mt-2">Semua agenda mengajar Anda sudah dilengkapi dengan data absensi dan semua jadwal sudah ada agendanya.</p>
                </div>
            )}
        </div>
      </div>
    );
});

export default AttendanceView;
